import _ from 'lodash'
import config from 'scoreboard/components/config'
import moment from 'moment'
import momentHelper from 'scoreboard/components/moment-helper'
import permissions from 'scoreboard/components/permissions'
import { globalPreventRules }  from 'scoreboard/components/paste/default-paste-allowed'

/**
 * Returns whether a paste is allowed
 * @param item the node to be pasted
 * @param parent the new parent for the node (null if pasting to a top-level org)
 * @param emptyOrg true if the org is empty
 * @param mode either 'copy' or 'move'
 * @param org the org the destination paste occurs in
 */
function scorecardsPasteAllowed(item, parent, emptyOrg, mode, org) {
	if (globalPreventRules.apply(this, arguments)) {
		return false
	}

	// #24141 not allowed to move nodes that are pending deletion
	if (_.get(item, 'pendingDeletion') || _.get(parent, 'pendingDeletion')) {
		return false
	}

	//replication rule supersede all other situations so they go first
	//replicated objects are never allowed to be moved
	//dereferenced items cannot be copied (only moved)
	if (!_.isNil(item.syncId) || (item.dereferenced && mode == 'copy')) {
		return false
	}

	//transformed docking nodes are the only type of replicated object allowed to have children
	//dereferenced can never be the target of a paste
	if (parent && ((!_.isNil(parent.syncId) && parent.originallyDocked != true) || parent.dereferenced)) {
		return false
	}

	// don't check whether the node is locked here, because we won't always
	// have the information for determining that at this point, and it
	// won't always be necessary to check #9199 #13072

	// only root nodes are allowed in empty orgs
	if (!parent) {
		return item.type == 'root' && !!emptyOrg
	}
	// root nodes can only be moved to empty orgs
	if (item.type == 'root') {
		return emptyOrg
	}

	// linked items can't have children
	if (parent.type == 'linked') {
		return false
	}
	// only metrics or linked metrics (#25696) can be under metrics
	if (parent.type == 'metric') {
		return item.type == 'metric' || item.sourceType == 'metric'
	}
	//docking nodes can never have children
	if (parent.type == 'docking') {
		return false
	}

	return true
}

/**
 * Returns whether the scorecard item is locked
 * @param item the item to examine
 * @param tree the kalpa-tree containing the scorecard item
 */
function locked(item, tree) {
	if (_.isNil(config.daysUntilScorecardItemLock)) {
		return false
	}

	if (permissions.appAdmin()) {
		return false
	}

	var archive = earliestArchive(item, tree)
	if (_.isNil(archive)) {
		return false
	}

	return moment().isAfter(moment(archive).add(config.daysUntilScorecardItemLock, 'days'))
}

/**
 * Returns the earliest archive date of the given scorecard item or one of its
 * ancestors
 * @param item the item to examine
 * @param tree the kalpa-tree containing the scorecard item
 * @returns {moment} the earliest archive date
 */
function earliestArchive(item, tree) {
	var earliestArchive = item.archive
	var parent = item
	while (!_.isNil(parent)) {
		parent = tree.parent(parent.id)
		var parentArchive = _.get(parent, 'archive')
		if (_.isNil(earliestArchive)) {
			earliestArchive = parentArchive
		} else if (!_.isNil(parentArchive)) {
			if (_.isString(earliestArchive)) {
				earliestArchive = moment(earliestArchive, momentHelper.isoDateFormat)
			}
			parentArchive = moment(parentArchive, momentHelper.isoDateFormat)
			// if we reach here, both earliestArchive and parentArchive are
			// specified
			if (parentArchive.isBefore(earliestArchive)) {
				earliestArchive = parentArchive
			}
		}
	}
	return earliestArchive
}

export default scorecardsPasteAllowed
export { locked }

scorecardsPasteAllowed.__filename__ = 'scorecards/edit/scorecards-paste-allowed.js'
